// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — where a headset fly-to leaves the globe: the place turned to face
 * the viewer, with north up as they see it.
 *
 * The fly-to used to end on `Quaternion.setFromUnitVectors(place, toViewer)`,
 * the shortest turn that brings the place round. That turn says nothing
 * about which way north points afterwards, so a place far from the
 * equator came round rolled. Europe arrived about 45° anticlockwise, Spain
 * at the bottom and Poland at the top (owner, 2026-11-04). This keeps the
 * same facing and settles the roll: the place's local north (the meridian
 * towards the pole) points to the viewer's up.
 *
 * Pure: no scene, no session. vrSession.flyToOnGlobe hands in vectors
 * already in the globe's parent frame (a placed globe may sit under a
 * rotated parent, as vrGlobeTilt allows for).
 */

import type * as THREE from 'three'

/**
 * The globe's local unit vector for (lat, lng): +X is longitude 0, +Y the
 * North Pole, and Z negated as THREE's SphereGeometry wraps an
 * equirectangular picture (the convention photorealEarth's sun uses).
 */
export function latLngToGlobeLocal(THREE_: typeof THREE, lat: number, lng: number): THREE.Vector3 {
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180
  return new THREE_.Vector3(
    Math.cos(latRad) * Math.cos(lngRad),
    Math.sin(latRad),
    -Math.cos(latRad) * Math.sin(lngRad),
  )
}

/** Below this squared length a projected "up" has no direction (a pole, or a viewer straight above). */
const DEGENERATE = 1e-8

/**
 * The globe orientation (in its parent's frame) that turns `place` (a unit
 * vector in the globe's own frame) onto `toViewer` (unit, from the globe's
 * centre towards the viewer) and the place's north onto `up` (the viewer's
 * up, the same frame as `toViewer`).
 *
 * At a pole, or with the viewer straight above or below the globe, north
 * has no direction to point; the shortest turn is used there, as before.
 */
export function northUpFacing(
  THREE_: typeof THREE,
  place: THREE.Vector3,
  toViewer: THREE.Vector3,
  up: THREE.Vector3,
): THREE.Quaternion {
  const pole = new THREE_.Vector3(0, 1, 0)
  // North along the surface at the place, and up across the viewer's line of sight.
  const placeNorth = pole.clone().addScaledVector(place, -pole.dot(place))
  const viewerUp = up.clone().addScaledVector(toViewer, -up.dot(toViewer))
  if (placeNorth.lengthSq() < DEGENERATE || viewerUp.lengthSq() < DEGENERATE) {
    return new THREE_.Quaternion().setFromUnitVectors(place, toViewer)
  }
  placeNorth.normalize()
  viewerUp.normalize()
  // Two right-handed frames (out, up, side); the turn maps the first onto the second.
  const from = new THREE_.Matrix4().makeBasis(place, placeNorth, new THREE_.Vector3().crossVectors(placeNorth, place))
  const to = new THREE_.Matrix4().makeBasis(toViewer, viewerUp, new THREE_.Vector3().crossVectors(viewerUp, toViewer))
  // `from` is orthonormal, so its inverse is its transpose.
  return new THREE_.Quaternion().setFromRotationMatrix(to.multiply(from.transpose()))
}
